import { useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  ALERT_CHANNELS,
  ALERT_CHANNEL_LABELS,
  SMS_AVAILABLE,
  accountEmailAvailable,
  alertEventDefinition,
  alertEventLabel,
  changedPolicyRows,
  fetchAlertChannelPolicy,
  isLockedChannel,
  quietHoursChanged,
  quietHoursExemptLabels,
  upsertAlertChannelPolicy,
  upsertAlertQuietHours,
  validateQuietHours,
  type AlertChannel,
  type AlertChannelPolicyLoad,
  type AlertChannelPolicyRow,
  type AlertQuietHours,
} from "../data/alert-channel-policy";
import { useReadModel } from "../data/useReadModel";

// Admin -> Alerts & channel policies (product lock 2026-08-07 §7, design D4, plate B).
//
// Rows are events, columns are channels, because an admin thinks "what happens when someone misses
// a checkpoint" and wants to read the answer across. The account floor is stated once at the top
// instead of repeating a lock icon on every row.
//
// The matrix is built from what the SERVER returns, never from a client-side catalog of defaults.
// A key the server does not send is not a row here, and a server without these RPCs gets an honest
// "not available yet" panel - switches an admin can flip but nothing can save read as configured.

export function AlertChannelPolicyPanel({
  client,
  accountId,
  canView = false,
  canManage = false,
  defaultTimeZone,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  /** policy.view capability - fail closed. */
  canView?: boolean;
  /** policy.manage capability - write affordances only exist when true. */
  canManage?: boolean;
  /** Account display zone, offered as the quiet-hours default. */
  defaultTimeZone?: string;
  pollMs?: number;
}) {
  if (!canView) {
    return null;
  }
  return (
    <AlertChannelPolicyPanelInner
      client={client}
      accountId={accountId}
      canManage={canManage}
      {...(defaultTimeZone !== undefined ? { defaultTimeZone } : {})}
      {...(pollMs !== undefined ? { pollMs } : {})}
    />
  );
}

/** Split so the read only mounts behind the canView gate. */
function AlertChannelPolicyPanelInner({
  client,
  accountId,
  canManage,
  defaultTimeZone,
  pollMs,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  defaultTimeZone?: string;
  pollMs?: number;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<AlertChannelPolicyLoad>(
    () => fetchAlertChannelPolicy(client, accountId),
    [client, accountId, reloadNonce],
    pollMs !== undefined ? { pollMs } : {},
  );

  if (status === "loading" && data === null) {
    return (
      <p className="surface__state" role="status">
        Loading alert and channel policies...
      </p>
    );
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load alert and channel policies."}
      </p>
    );
  }
  if (data === null) {
    return null;
  }
  if (data.status === "unavailable") {
    return (
      <p className="surface__state" role="status">
        {data.reason}
      </p>
    );
  }
  if (data.rows.length === 0) {
    return (
      <p className="surface__state" role="status">
        No alert events are configured for this account yet.
      </p>
    );
  }

  return (
    <AlertPolicyEditor
      client={client}
      accountId={accountId}
      canManage={canManage}
      rows={data.rows}
      quietHours={data.quietHours}
      {...(defaultTimeZone !== undefined ? { defaultTimeZone } : {})}
      onSaved={() => setReloadNonce((nonce) => nonce + 1)}
    />
  );
}

/** Baseline after the server accepts one event. Rows that have not been accepted stay put. */
function acceptSavedPolicyRow(
  baseline: readonly AlertChannelPolicyRow[],
  saved: AlertChannelPolicyRow,
): readonly AlertChannelPolicyRow[] {
  const index = baseline.findIndex((row) => row.eventKey === saved.eventKey);
  if (index < 0) return [...baseline, saved];
  const next = baseline.slice();
  next[index] = saved;
  return next;
}

function AlertPolicyEditor({
  client,
  accountId,
  canManage,
  rows,
  quietHours,
  defaultTimeZone,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  rows: readonly AlertChannelPolicyRow[];
  quietHours: AlertQuietHours;
  defaultTimeZone?: string;
  onSaved: () => void;
}) {
  const [draftRows, setDraftRows] = useState<readonly AlertChannelPolicyRow[]>(rows);
  const [draftQuiet, setDraftQuiet] = useState<AlertQuietHours>(quietHours);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  // Last snapshot this form accepted. A refresh is adopted only when the draft still matches it.
  // Comparing the draft to the incoming payload instead makes a pristine form look dirty and
  // write that stale draft back over the refresh.
  const baselineRowsRef = useRef(rows);
  const baselineQuietRef = useRef(quietHours);
  const draftRowsRef = useRef(draftRows);
  const draftQuietRef = useRef(draftQuiet);
  // Publish draft changes when they are queued, before the next render. A pending passive
  // refresh effect can run after a click but before that render, and must see the edit.

  useEffect(() => {
    if (changedPolicyRows(baselineRowsRef.current, draftRowsRef.current).length === 0) {
      baselineRowsRef.current = rows;
      draftRowsRef.current = rows;
      setDraftRows(rows);
    }
    if (!quietHoursChanged(baselineQuietRef.current, draftQuietRef.current)) {
      baselineQuietRef.current = quietHours;
      draftQuietRef.current = quietHours;
      setDraftQuiet(quietHours);
    }
  }, [rows, quietHours]);

  const dirty =
    changedPolicyRows(baselineRowsRef.current, draftRows).length > 0 ||
    quietHoursChanged(baselineQuietRef.current, draftQuiet);
  const exemptLabels = useMemo(() => quietHoursExemptLabels(rows), [rows]);

  function setChannel(eventKey: string, channel: AlertChannel, next: boolean): void {
    const nextRows = draftRowsRef.current.map((row) =>
      row.eventKey === eventKey ? { ...row, [channel]: next } : row,
    );
    draftRowsRef.current = nextRows;
    setDraftRows(nextRows);
    setFailure(null);
    setReceipt(null);
  }

  function updateQuiet(patch: Partial<AlertQuietHours>): void {
    const nextQuiet = { ...draftQuietRef.current, ...patch };
    draftQuietRef.current = nextQuiet;
    setDraftQuiet(nextQuiet);
    setFailure(null);
    setReceipt(null);
  }

  function discard(): void {
    baselineRowsRef.current = rows;
    baselineQuietRef.current = quietHours;
    draftRowsRef.current = rows;
    draftQuietRef.current = quietHours;
    setDraftRows(rows);
    setDraftQuiet(quietHours);
    setFailure(null);
    setReceipt(null);
  }

  async function submit(): Promise<void> {
    if (busy || !canManage) return;

    const validation = validateQuietHours(draftQuiet);
    if (!validation.ok) {
      setFailure(validation.reason);
      return;
    }

    setBusy(true);
    setFailure(null);
    setReceipt(null);

    // One RPC per changed event row: the server owns the per-key refusals, so a partial failure
    // must name the event that stopped rather than claim the whole matrix saved. The diff is
    // against the accepted baseline, not the latest payload, so a refresh during an edit is
    // not written back. Each row the server accepts advances that row's baseline immediately.
    // A later refusal must not leave the saved row looking like the old baseline: undoing it
    // and retrying would then skip the undo and still report success.
    const savedRows = baselineRowsRef.current;
    const savedQuiet = baselineQuietRef.current;
    let acceptedRows = savedRows;
    for (const row of changedPolicyRows(savedRows, draftRows)) {
      const outcome = await upsertAlertChannelPolicy(client, accountId, row);
      if (!outcome.ok) {
        baselineRowsRef.current = acceptedRows;
        setFailure(`${alertEventLabel(row.eventKey)}: ${outcome.reason}`);
        setBusy(false);
        onSaved();
        return;
      }
      acceptedRows = acceptSavedPolicyRow(acceptedRows, row);
      baselineRowsRef.current = acceptedRows;
    }

    if (quietHoursChanged(savedQuiet, draftQuiet)) {
      const outcome = await upsertAlertQuietHours(client, accountId, draftQuiet);
      if (!outcome.ok) {
        setFailure(outcome.reason);
        setBusy(false);
        onSaved();
        return;
      }
      baselineQuietRef.current = draftQuiet;
    }

    baselineRowsRef.current = draftRows;
    baselineQuietRef.current = draftQuiet;
    setReceipt("Alert and channel policies saved.");
    setBusy(false);
    onSaved();
  }

  return (
    <div className="operator-panel alerts-policy">
      <p className="alerts-policy__floor">
        <strong>Account rules win.</strong> Everyone in this account receives at least what is
        switched on here. Members can add notifications for themselves. Messages push is the one
        exception: each member may switch it off for themselves.
      </p>

      <table className="commercial__table alerts-policy__matrix">
        <caption className="alerts-policy__caption">
          Where each event goes - {draftRows.length} {draftRows.length === 1 ? "event" : "events"} ·{" "}
          {ALERT_CHANNELS.length} channels
        </caption>
        <thead>
          <tr>
            <th scope="col">Event</th>
            {ALERT_CHANNELS.map((channel) => (
              <th key={channel} scope="col" className="alerts-policy__col">
                {ALERT_CHANNEL_LABELS[channel]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {draftRows.map((row) => {
            const definition = alertEventDefinition(row.eventKey);
            return (
              <tr key={row.eventKey}>
                <th scope="row" className="alerts-policy__event">
                  <span className="alerts-policy__event-name">{alertEventLabel(row.eventKey)}</span>
                  {definition !== "" ? (
                    <span className="alerts-policy__event-def">{definition}</span>
                  ) : null}
                </th>
                {ALERT_CHANNELS.map((channel) => (
                  <td key={channel} className="alerts-policy__cell">
                    <ChannelSwitch
                      channel={channel}
                      eventKey={row.eventKey}
                      checked={row[channel]}
                      disabled={!canManage || busy}
                      onChange={(next) => setChannel(row.eventKey, channel, next)}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <ul className="alerts-policy__legend" aria-label="Switch states">
        <li>
          <span className="alerts-policy__sw alerts-policy__sw--locked" aria-hidden="true" />
          Locked on by policy
        </li>
        <li>
          <span className="alerts-policy__sw alerts-policy__sw--on" aria-hidden="true" />
          On - account delivery enabled
        </li>
        <li>
          <span className="alerts-policy__sw" aria-hidden="true" />
          Off - account delivery disabled
        </li>
        <li>
          <span className="alerts-policy__sw alerts-policy__sw--unavailable" aria-hidden="true" />
          Not available - SMS anywhere, email on Messages
        </li>
      </ul>

      <div className="alerts-policy__cards">
        <section className="alerts-policy__card" aria-label="Quiet hours">
          <h3 className="alerts-policy__card-title">Quiet hours</h3>
          <QuietHoursEditor
            busy={busy}
            canManage={canManage}
            draft={draftQuiet}
            exemptLabels={exemptLabels}
            onChange={updateQuiet}
            {...(defaultTimeZone !== undefined ? { defaultTimeZone } : {})}
          />
        </section>

        <section className="alerts-policy__card" aria-label="SMS">
          <h3 className="alerts-policy__card-title">
            SMS <span className="rollout__badge rollout__badge--disabled">Coming soon</span>
          </h3>
          <p className="alerts-policy__card-body">
            SMS delivery is not available yet. When it ships, these switches unlock and your current
            choices carry over untouched.
          </p>
        </section>
      </div>

      {canManage ? (
        <div className="policy__editor-actions">
          <button type="button" className="minter__confirm-go" disabled={busy || !dirty} onClick={() => void submit()}>
            {busy ? "Saving..." : "Save changes"}
          </button>
          <button type="button" className="builder__navbtn" disabled={busy || !dirty} onClick={discard}>
            Discard changes
          </button>
        </div>
      ) : (
        <p className="page__note">Read-only for this role.</p>
      )}

      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
      {receipt !== null ? (
        <p className="surface__state surface__state--ok" role="status">
          {receipt}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One matrix cell. Four states, matching the legend: locked (safety floor, cannot be switched
 * off), on, off, and unavailable (SMS). Unavailable is deliberately NOT rendered as a plain
 * greyed-out off switch - "a greyed-out toggle with no explanation reads as broken", so it gets a
 * dashed treatment plus the SMS card beside the matrix.
 */
function ChannelSwitch({
  channel,
  eventKey,
  checked,
  disabled,
  onChange,
}: {
  channel: AlertChannel;
  eventKey: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  const name = `${ALERT_CHANNEL_LABELS[channel]} for ${alertEventLabel(eventKey)}`;
  const unavailable = (channel === "sms" && !SMS_AVAILABLE)
    || (channel === "email" && !accountEmailAvailable(eventKey));
  const unavailableReason = channel === "sms" ? "coming soon" : "not available";
  const locked = isLockedChannel(eventKey, channel);

  if (unavailable) {
    return (
      <span
        className="alerts-policy__sw alerts-policy__sw--unavailable"
        role="img"
        aria-label={`${name}: ${unavailableReason}`}
      />
    );
  }

  if (locked) {
    return (
      <span
        className="alerts-policy__sw alerts-policy__sw--locked"
        role="img"
        aria-label={`${name}: locked on by policy`}
      />
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={name}
      disabled={disabled}
      className={`alerts-policy__sw alerts-policy__sw--button ${checked ? "alerts-policy__sw--on" : ""}`}
      onClick={() => onChange(!checked)}
    />
  );
}

function QuietHoursEditor({
  busy,
  canManage,
  draft,
  defaultTimeZone,
  exemptLabels,
  onChange,
}: {
  busy: boolean;
  canManage: boolean;
  draft: AlertQuietHours;
  defaultTimeZone?: string;
  exemptLabels: readonly string[];
  onChange: (patch: Partial<AlertQuietHours>) => void;
}) {
  const zones = useMemo(() => timeZoneOptions(draft.timeZone, defaultTimeZone), [draft.timeZone, defaultTimeZone]);
  const exceptionSentence =
    exemptLabels.length === 0
      ? "Safety events always deliver, whatever this window says."
      : `${exemptLabels.join(" and ")} always deliver, whatever this window says.`;

  return (
    <div className="alerts-policy__quiet">
      <button
        type="button"
        role="switch"
        aria-checked={draft.enabled}
        aria-label="Quiet hours"
        disabled={!canManage || busy}
        className={`alerts-policy__sw alerts-policy__sw--button ${draft.enabled ? "alerts-policy__sw--on" : ""}`}
        onClick={() =>
          onChange({
            enabled: !draft.enabled,
            // Offer a usable window the moment it is switched on, rather than an invalid blank one.
            ...(draft.enabled
              ? {}
              : {
                  startLocal: draft.startLocal ?? "23:00",
                  endLocal: draft.endLocal ?? "06:00",
                  timeZone: draft.timeZone ?? defaultTimeZone ?? null,
                }),
          })
        }
      />
      <div className="alerts-policy__quiet-copy">
        <p className="alerts-policy__card-body">
          {draft.enabled
            ? "Hold non-urgent push inside the window below, local to the zone you choose."
            : "Off. Every event delivers on its channels around the clock."}
        </p>
        <p className="alerts-policy__quiet-exception">{exceptionSentence}</p>
      </div>

      {draft.enabled ? (
        <div className="alerts-policy__quiet-fields">
          <label className="duty-policy__field">
            <span className="duty-policy__field-label">Start</span>
            <input
              type="time"
              value={draft.startLocal ?? ""}
              disabled={!canManage || busy}
              onChange={(event) => onChange({ startLocal: emptyToNull(event.target.value) })}
            />
          </label>
          <label className="duty-policy__field">
            <span className="duty-policy__field-label">End</span>
            <input
              type="time"
              value={draft.endLocal ?? ""}
              disabled={!canManage || busy}
              onChange={(event) => onChange({ endLocal: emptyToNull(event.target.value) })}
            />
          </label>
          <label className="duty-policy__field">
            <span className="duty-policy__field-label">Time zone</span>
            <select
              value={draft.timeZone ?? ""}
              disabled={!canManage || busy}
              onChange={(event) => onChange({ timeZone: emptyToNull(event.target.value) })}
            >
              <option value="">Choose a time zone</option>
              {zones.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
    </div>
  );
}

/**
 * IANA zones for the picker. A free-text zone is a typo waiting to become a silently wrong
 * delivery window, so this offers the runtime's own list where available and falls back to the
 * values already in play.
 */
function timeZoneOptions(current: string | null, fallback?: string): string[] {
  const supported =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  const zones = supported.length > 0 ? [...supported] : [];
  for (const zone of [fallback, current]) {
    if (zone !== undefined && zone !== null && zone.length > 0 && !zones.includes(zone)) {
      zones.push(zone);
    }
  }
  return zones;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
